import { CAMERA_LINKS, cameraSrc, type BayCamera } from './cameras';
import { embedFrame, openCameraLightbox } from './lightbox';
import { probeCamera } from './cameraStatus';

/** Caltrans overwrites each frame about once a minute. */
const REFRESH_MS = 60_000;

/**
 * A live look at the water, next to the numbers.
 *
 * The image is refreshed on a timer rather than left to the browser cache, and
 * the previous frame is held until the next one decodes so the panel never
 * flashes empty on a slow fetch.
 */
export function buildCameraPopup(camera: BayCamera, onClose: () => void): HTMLElement {
  const root = document.createElement('div');
  root.className = 'popup';

  root.innerHTML = `
    <header class="popup-head">
      <button type="button" class="popup-close" aria-label="Close camera">×</button>
      <h2>${camera.name}</h2>
      <p class="popup-sub">
        <span class="badge badge-camera">Camera</span>
        <span class="muted">${camera.view}</span>
      </p>
    </header>
    <figure class="cam-figure">
      <button type="button" class="cam-expand" aria-label="Expand ${camera.name}">
        Expand ⤢
      </button>
      ${camera.embedUrl
        // The operator publishes a live player; embed that rather than reaching
        // past it for the raw stream. It lays out at a fixed size, so it is
        // rendered full size and scaled down to the panel — sizing the iframe
        // to the panel instead just cropped the player.
        ? `<div class="cam-embed-scaler">
             <iframe
               class="cam-embed"
               src="${camera.embedUrl}"
               title="Live view: ${camera.view}"
               loading="lazy"
               scrolling="no"
               referrerpolicy="no-referrer-when-downgrade"
               width="${embedFrame(camera).width}"
               height="${embedFrame(camera).height}"
             ></iframe>
           </div>`
        : `<img class="cam-image" alt="Live view: ${camera.view}" decoding="async">
           <figcaption class="cam-caption">
             <span class="cam-age">Loading…</span>
             <button type="button" class="cam-refresh">Refresh</button>
           </figcaption>`}
    </figure>
    <p class="cam-credit">
      Camera operated by ${camera.credit}.
      ${camera.embedUrl
        ? 'Live view, embedded from the operator.'
        : 'Images update roughly once a minute and can go dark for maintenance.'}
    </p>
    <section class="cam-links">
      <h3>Other Bay cameras</h3>
      ${CAMERA_LINKS.map((l) => `
        <a class="source-link" href="${l.href}" target="_blank" rel="noopener">
          ${l.name} ↗ <span class="muted">${l.note}</span>
        </a>`).join('')}
    </section>
    <a class="source-link" href="https://cwwp2.dot.ca.gov/vm/streamlist.htm"
       target="_blank" rel="noopener">All Caltrans Bay Area cameras ↗</a>`;

  root.querySelector<HTMLButtonElement>('.popup-close')!.addEventListener('click', onClose);

  // The panel frame is too small to read sea state off; clicking the picture
  // opens it at whatever size the source actually supports.
  const expand = () => openCameraLightbox(camera);
  root.querySelector<HTMLButtonElement>('.cam-expand')!.addEventListener('click', expand);
  // An iframe swallows clicks, so only the still image is itself clickable.
  root.querySelector<HTMLImageElement>('.cam-image')?.addEventListener('click', expand);

  // Scale the player down to whatever width the panel actually has.
  //
  // A ResizeObserver rather than requestAnimationFrame: this element has no
  // width until it is inserted, and rAF never fires at all in a background tab,
  // which left the player unscaled. The observer also re-fits when the panel
  // becomes a bottom sheet on mobile.
  const scaler = root.querySelector<HTMLElement>('.cam-embed-scaler');
  if (scaler) {
    const frame = embedFrame(camera);
    const inner = scaler.querySelector<HTMLIFrameElement>('.cam-embed')!;

    const fit = (available: number) => {
      if (available <= 0) return;
      const scale = Math.min(available / frame.width, 1);
      inner.style.transform = `scale(${scale})`;
      // The scaled iframe still reserves its full box, so collapse the gap.
      scaler.style.height = `${Math.round(frame.height * scale)}px`;
    };

    // Fit once as soon as the panel inserts us — deterministic, and it does not
    // wait on a frame that a background tab will never render. The observer then
    // handles later changes, such as the panel becoming a bottom sheet.
    root.addEventListener('mount', () => fit(scaler.clientWidth));
    new ResizeObserver(([entry]) => fit(entry.contentRect.width)).observe(scaler);
  }

  // An embedded player refreshes itself; only the still-frame cameras need a timer.
  const img = root.querySelector<HTMLImageElement>('.cam-image');
  const age = root.querySelector<HTMLElement>('.cam-age');
  if (!img || !age) return root;

  const timeFmt = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });

  let timer: number | undefined;

  const load = () => {
    const at = new Date();
    // Decode into a detached image first, so a failed or slow fetch leaves the
    // frame already on screen untouched.
    const next = new Image();
    next.crossOrigin = 'anonymous';
    next.onload = () => {
      img.src = next.src;
      img.classList.add('is-loaded');
      // Caltrans serves its outage graphic with HTTP 200, so a loaded frame is
      // not proof of a working camera — say so rather than presenting a picture
      // of the words "Temporarily Unavailable" as a live view.
      void probeCamera(camera).then((state) => {
        const down = state === 'offline';
        root.classList.toggle('cam-is-offline', down);
        age.textContent = down
          ? 'Camera offline — Caltrans is serving its unavailable notice'
          : `Updated ${timeFmt.format(at)}`;
      });
    };
    next.onerror = () => {
      age.textContent = img.classList.contains('is-loaded')
        ? 'Could not refresh — showing the last frame'
        : 'Camera unavailable';
    };
    next.src = cameraSrc(camera, at);
  };

  root.querySelector<HTMLButtonElement>('.cam-refresh')!.addEventListener('click', load);

  load();
  timer = window.setInterval(load, REFRESH_MS);

  // The panel is discarded on close, so the timer has to go with it.
  const observer = new MutationObserver(() => {
    if (!root.isConnected) {
      window.clearInterval(timer);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return root;
}
